<template lang="pug">
b-form
  // Status Display - Show when already bound
  template(v-if="isInitialized && showOtpTelegramBound")
    b-alert.mb-0(variant="success" show)
      .d-flex.justify-content-between.align-items-center
        strong
          i.fas.fa-check-circle.mr-2
          | {{ $t("ui.telegram_bot_bound") }}
        SubmitButton(
          :loading="loading"
          icon="fa fa-unlock"
          :text="$t('ui.unbind')"
          variant="danger"
          isModal=false
          type="button"
          @click="unbindTelegram()"
          :disabled="$helpers.isSuspended()"
        )
    b-alert.mt-3(variant="warning" show)
      strong
        i.fas.fa-exclamation-triangle.mr-2
        | {{ $t("message.otp_unbind_first_to_use_other", { module: $t("ui.setup_otp_via_mobile_authenticator") }) }}

  // Telegram Binding Flow - Show when not bound
  template(v-else-if="isInitialized")
    // Section Heading
    //- .form-group.mb-4
    //-   h6.font-weight-bold.mb-3 {{ $t("ui.setup_otp_via_telegram_chatbot") }}
    //-   p.mb-3 {{ $t("message.telegram_chatbot_description") }}
    //-   p.mb-4 {{ $t("message.telegram_setup_instructions") }}

    // Step 1: Download Telegram
    .form-group.mb-5
      h6.font-weight-bold.mb-3 {{ $t("ui.step_1_download_telegram") }}
      .telegram-section
        .telegram-branding
          .d-flex
            .telegram-logo
              img(
                src="/assets/img/telegram_bot.png"
                alt="Telegram Bot"
              )
            .brand-content
              .brand-text.mb-3
                .brand-name Telegram
                .brand-subtitle Telegram Messenger Inc.
              .download-buttons.d-flex.flex-wrap
                a.download-btn.app-store(
                  href="https://apps.apple.com/app/telegram-messenger/id686449807"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  img(
                    src="/assets/img/appstore.png"
                    alt="Download on the App Store"
                  )
                a.download-btn.google-play(
                  href="https://play.google.com/store/apps/details?id=org.telegram.messenger"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  img(
                    src="/assets/img/googleplay.png"
                    alt="Get it on Google play"
                  )

    // Step 2: Generate QR Code
    .form-group.mb-5
      h6.font-weight-bold.mb-3 {{ $t("ui.step_2_signup_and_generate_qr") }}

    // Step 3: Enable Telegram Bot
    .form-group.mb-5
      h6.font-weight-bold.mb-3 {{ $t("ui.step_3_enable_telegram_bot") }}
        .generate-qr-section
          b-button.mb-3(
            variant="primary"
            @click="verifyAndBindTelegram()"
            :disabled="loading"
          )
            | {{ $t("ui.enable_otp_via_telegram_chatbot") }}
          //- .enable-checkbox.mt-3
          //-   b-form-checkbox(v-model="enableTelegramOTP")
          //-     | {{ $t("ui.enable_otp_via_telegram_chatbot") }}
          
          // Show QR Code if generated (from Step 2)
          .qr-code-container.text-center.mb-3.mt-4(v-if="telegramQRCode")
            img.qr-code-image(
              :src="telegramQRCode"
              alt="Telegram QR Code"
            )

    // Error Message
    b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}

  // Unbind Telegram Modal 
  UnbindOTPTelegram(ref="unbindOTPTelegramModal" @unbind-success="onUnbindSuccess")
</template>

<script>
import config from "@/config";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";
import UnbindOTPTelegram from "@/components/settings/unbindOTPTelegram";

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput,
    UnbindOTPTelegram
  },
  data() {
    return {
      isInitialized: false,
      showOtpTelegramBound: false,
      telegramBotUsername: null,
      telegram_code: "",
      telegramQRCode: null,
      showTelegramQR: false,
      enableTelegramOTP: false,
      loading: false,
      submission: {
        success: true,
        status: ""
      },
      allowUnbind: true,
      statusPollInterval: null
    };
  },
  computed: {
    getOtpTelegramBound() {
      return this.$store.getters.getOtpTelegramBound;
    },
    is2faBound() {
      const twoFactorStatus = this.$store.getters.get2faValidated;
      return twoFactorStatus === true || twoFactorStatus === 1;
    }
  },
  created() {
    this.showOtpTelegramBound = this.getOtpTelegramBound === true || this.getOtpTelegramBound === 1;
  },
  validations: {
    telegram_code: {
      required,
      minLength: minLength(4),
      maxLength: maxLength(10)
    }
  },
  mounted() {
    // Don't load if 2FA is bound
    if (!this.is2faBound) {
      this.loadBindingStatus();
    }
  },
  beforeDestroy() {
    // Clean up polling and event listeners
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  },
  methods: {
    reset() {
      this.$v.$reset();
      this.submission.success = true;
      this.submission.status = "";
    },
    
    async loadBindingStatus() {
      // Load current binding status from backend
      try {
        const response = await this.$store.dispatch("user/getOTPTelegram");
        if (response.success && response.data) {
          const t_enabled = response.data[0]['t_enabled'];
          this.showOtpTelegramBound = t_enabled === true;
          // Update store state to ensure reactivity in parent components
          this.$set(this.$store.state.user.profile, 't_enabled', t_enabled);
        } else {
          this.showOtpTelegramBound = false;
          // Update store state
          this.$set(this.$store.state.user.profile, 't_enabled', false);
        }
      } catch (error) {
        console.error("Error loading binding status:", error);
        this.showOtpTelegramBound = false;
        // Update store state
        this.$set(this.$store.state.user.profile, 't_enabled', false);
      } finally {
        this.isInitialized = true; 
      }
    },        
    
    async verifyAndBindTelegram() {
      // This method is called when binding is needed
      // It may require a code from Telegram, or it might be automatic
      // Adjust based on your backend API requirements
      this.loading = true;
      
      try {
        // If code is required, use bindTelegram
        // Otherwise, the binding might happen automatically via setupTelegram
        // https://t.me/PrototypeXChatBot?start=
        const response = await this.$store.dispatch("user/verifyOTPTelegram");
        if (response.success) {
          // Update store state immediately after successful binding
          // Note: The actual binding happens after user clicks the Telegram link,
          // but we update the store when polling detects it's bound
          this.$swal({
            title: this.$t("ui.succeed"),
            text: this.$t("message.telegram_otp_bound"),
            type: "success",
            allowOutsideClick: false
          }).then(() => {
            const telegramLink = response.data[0].telegram_link;
            if (telegramLink) {
              // Open in new window with proper features
              const newWindow = window.open(telegramLink, "_blank", "noopener,noreferrer");
              
              // Start polling to check binding status after opening Telegram
              // Clear any existing polling first
              if (this.statusPollInterval) {
                clearInterval(this.statusPollInterval);
              }
              
              // Poll every 2 seconds for up to 60 seconds (30 checks)
              let pollCount = 0;
              const maxPolls = 30;
              this.statusPollInterval = setInterval(async () => {
                pollCount++;
                await this.loadBindingStatus();
                
                // Stop polling if binding is complete or max polls reached
                if (this.showOtpTelegramBound || pollCount >= maxPolls) {
                  clearInterval(this.statusPollInterval);
                  this.statusPollInterval = null;
                }
              }, 2000);
              
              // Also check immediately (in case binding was instant)
              this.loadBindingStatus();
              
              // Check if popup was blocked
              // if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
              //   // Popup blocked - show message with clickable link
              //   this.$swal({
              //     title: this.$t("ui.status"),
              //     html: this.$t("message.telegram_otp_bound") + "<br><br>" + 
              //           "Please click the link below to open Telegram:<br>" +
              //           "<a href='" + telegramLink + "' target='_blank' style='color: #007bff; word-break: break-all; text-decoration: underline;'>" + telegramLink + "</a>",
              //     type: "info",
              //     allowOutsideClick: false
              //   });
              // }
            }
          });
        } else {
          this.submission.success = false;
          this.submission.status = this.$t("message." + (response.errorTag || "telegram_setup_failed"));
        }
      } catch (error) {
        this.$swal({
          title: this.$t("ui.error"),
          text: this.$t("message.telegram_setup_failed"),
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    
    unbindTelegram() {
      // Show modal to get OTP code
      this.$refs.unbindOTPTelegramModal.$refs.unbindOTPTelegramModal.show();
    },

    onUnbindSuccess() {
      // Update store state to reflect unbind (use Vue.set for reactivity)
      this.$set(this.$store.state.user.profile, 't_enabled', false);
      // Reload binding status to update UI
      this.loadBindingStatus();
    },

    // async unbindTelegram() {
    //   this.$swal({
    //     title: this.$t("ui.status"),
    //     text: this.$t("message.unbind_otp_confirm"),
    //     type: "warning",
    //     showCancelButton: true,
    //     allowOutsideClick: false
    //   }).then(async (result) => {
    //     if (result.value) {
    //       this.loading = true;
    //       try {
    //         const response = await this.$store.dispatch("user/unbindOTPTelegram");
    //         if (response.success) {
    //           this.$swal({
    //             title: this.$t("ui.succeed"),
    //             text: this.$t("message.otp.unbound"),
    //             type: "success",
    //               allowOutsideClick: false
    //           }).then(() => {
    //             this.loadBindingStatus();
    //           });
    //         } else {
    //           this.$swal({
    //             title: this.$t("ui.error"),
    //             text: this.$t("message." + response.errorTag),
    //             type: "error"
    //           });
    //         }
    //       } catch (error) {
    //         this.$swal({
    //           title: this.$t("ui.error"),
    //           text: this.$t("message.otp.unbindFailed"),
    //           type: "error"
    //         });
    //       } finally {
    //         this.loading = false;
    //       }
    //     }
    //   });
    // },
    
    cancelBinding() {
      this.telegramBotUsername = null;
      this.telegram_code = "";
      this.telegramQRCode = null;
      this.showTelegramQR = false;
      this.enableTelegramOTP = false;
      this.reset();
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  color: #1a73e8;
  text-decoration: none;
}

.cursor-pointer:hover {
  text-decoration: underline;
}

/* Telegram Section */
.telegram-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.telegram-branding {
  margin-bottom: 0;
}

.telegram-branding .d-flex {
  align-items: flex-start;
  gap: 16px;
}

/* Telegram Logo */
.telegram-logo {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding-top: 2px;
}

.telegram-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-width: 100px;
  max-height: 100px;
}

.brand-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.brand-text {
  margin-bottom: 12px;
}

.brand-name {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  color: #202124;
  margin-bottom: 2px;
}

.brand-subtitle {
  font-size: 0.875rem;
  color: #5f6368;
  line-height: 1.4;
}

/* Download Buttons */
.download-buttons {
  gap: 12px;
  align-items: center;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.2s ease;
}

.download-btn:hover {
  text-decoration: none;
  opacity: 0.85;
  transform: translateY(-1px);
}

.download-btn img {
  height: 40px;
  width: auto;
  display: block;
}

.show-qr-link {
  margin-top: 12px;
}

/* QR Code */
.qr-code-container {
  margin: 20px 0;
}

.qr-code-image {
  border: 2px solid #ddd;
  padding: 15px;
  background: white;
  max-width: 300px;
  width: 100%;
  border-radius: 4px;
}

/* Generate QR Section */
.generate-qr-section {
  padding: 16px 0;
}

/* Enable Checkbox */
.enable-checkbox {
  margin-top: 16px;
}

/* Form Groups */
.form-group {
  margin-bottom: 2rem;
}

.form-group.mb-5 {
  margin-bottom: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .telegram-section {
    padding: 20px 16px;
  }
  
  .download-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .download-btn {
    width: 100%;
    justify-content: center;
  }
  
  .download-btn img {
    height: 40px;
  }
  
  .telegram-branding .d-flex {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .telegram-logo {
    margin-bottom: 16px;
    align-items: center;
  }
  
  .brand-content {
    width: 100%;
    align-items: center;
  }
  
  .brand-text {
    text-align: center;
  }
  
  .download-buttons {
    justify-content: center;
  }
}
</style>

